/**
 * StationInteriorSystem.js
 *
 * June 2026 — Walkable Space Station Interiors.
 *
 * When the player docks their ship at an orbital station they disembark INTO a
 * walkable interior (built on demand at a far world offset so it never overlaps
 * the space/surface scenes). The hall is brightly lit, has a **trading
 * terminal** the player walks up to (press E → station trade overlay), a couple
 * of station-crew figures for life, a viewport looking out at the stars, and an
 * **airlock** to return to the ship.
 *
 * Mirrors CaveSystem's lifecycle so GameScene can reuse the same walking code
 * (it swaps the ground-height source to this flat floor while `inStation`).
 *
 * Public API:
 *   buildInterior(seed, name)   // construct the hall meshes (call on dock)
 *   clearInterior()             // dispose the hall meshes (call on undock)
 *   showInterior(visible)
 *   floorHeight(x,z)            // flat station deck height
 *   clamp(x,z)                  // keep the player inside the hall walls
 *   getEntryPoint()             // Vector3 spawn position inside the hall
 *   getExitPos()                // airlock position
 *   getNearExit(radius)         // -> { distance } | null
 *   getTerminalPos()            // trade terminal position
 *   getNearTerminal(radius)     // -> { distance } | null
 *   tick(dt)                    // animate terminal hologram
 *   dispose()
 */

import {
  Vector3,
  Color3,
  MeshBuilder,
  StandardMaterial,
  TransformNode,
  PointLight,
} from '@babylonjs/core';

// Hall lives far from both the surface (origin) and caves (5000,5000).
const SX = 8000;
const SZ = 8000;
const HALF_W = 20;   // half width  (x)
const HALF_D = 16;   // half depth  (z)
const WALL_H = 7;
const FLOOR_Y = 0;
const TERMINAL_RADIUS = 3.6;
const EXIT_RADIUS = 3.6;

function mkRng(seed) {
  let s = (seed | 0) >>> 0;
  if (!s) s = 0xa11ce;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

export default class StationInteriorSystem {
  constructor(scene, opts = {}) {
    this.scene = scene;
    this.getPlayerPos = opts.getPlayerPos || (() => new Vector3(0, 0, 0));
    this.glow = opts.glow || null;

    this.SX = SX;
    this.SZ = SZ;

    this._root = null;
    this._t = 0;
    this._holo = null;
    // Spawn mid-hall (NOT on the airlock) facing the trade terminal, so the
    // player must walk back to the airlock to leave (no instant re-exit).
    this._entryPos = new Vector3(SX, FLOOR_Y, SZ + HALF_D * 0.35);
    this._exitPos = new Vector3(SX, FLOOR_Y, SZ + HALF_D - 2.5);
    this._terminalPos = new Vector3(SX, FLOOR_Y, SZ - HALF_D + 3.5);
    this._shipyardPos = new Vector3(SX - HALF_W + 3.5, FLOOR_Y, SZ);
    this._skillsPos = new Vector3(SX + HALF_W - 3.5, FLOOR_Y, SZ);
  }

  floorHeight() { return FLOOR_Y; }

  clamp(x, z) {
    const lx = x - SX;
    const lz = z - SZ;
    const mx = HALF_W - 1.5;
    const mz = HALF_D - 1.5;
    return {
      x: SX + Math.max(-mx, Math.min(mx, lx)),
      z: SZ + Math.max(-mz, Math.min(mz, lz)),
    };
  }

  getEntryPoint() { return this._entryPos.clone(); }
  getExitPos() { return this._exitPos.clone(); }
  getTerminalPos() { return this._terminalPos.clone(); }
  getShipyardPos() { return this._shipyardPos.clone(); }
  getSkillsPos() { return this._skillsPos.clone(); }

  getNearShipyard(radius = TERMINAL_RADIUS) {
    const p = this.getPlayerPos();
    const d = Math.hypot(p.x - this._shipyardPos.x, p.z - this._shipyardPos.z);
    return d < radius ? { distance: d } : null;
  }

  getNearSkills(radius = TERMINAL_RADIUS) {
    const p = this.getPlayerPos();
    const d = Math.hypot(p.x - this._skillsPos.x, p.z - this._skillsPos.z);
    return d < radius ? { distance: d } : null;
  }

  getNearExit(radius = EXIT_RADIUS) {
    const p = this.getPlayerPos();
    const d = Math.hypot(p.x - this._exitPos.x, p.z - this._exitPos.z);
    return d < radius ? { distance: d } : null;
  }

  getNearTerminal(radius = TERMINAL_RADIUS) {
    const p = this.getPlayerPos();
    const d = Math.hypot(p.x - this._terminalPos.x, p.z - this._terminalPos.z);
    return d < radius ? { distance: d } : null;
  }

  _mat(name, diffuse, opts = {}) {
    const m = new StandardMaterial(name, this.scene);
    m.diffuseColor = diffuse;
    m.specularColor = opts.specular || new Color3(0.15, 0.16, 0.2);
    if (opts.emissive) m.emissiveColor = opts.emissive;
    if (opts.specularPower) m.specularPower = opts.specularPower;
    m.maxSimultaneousLights = 8;
    return m;
  }

  buildInterior(seed, name) {
    this.clearInterior();
    const rng = mkRng((seed | 0) >>> 0 || 7);
    const root = new TransformNode('stationInteriorRoot', this.scene);
    this._root = root;

    const deckMat = this._mat('stDeckMat', new Color3(0.26, 0.28, 0.34), { specular: new Color3(0.3, 0.32, 0.38), specularPower: 64 });
    const wallMat = this._mat('stWallMat', new Color3(0.34, 0.36, 0.42), { specular: new Color3(0.2, 0.22, 0.28) });
    const trimMat = this._mat('stTrimMat', new Color3(0.16, 0.18, 0.24), { emissive: new Color3(0.04, 0.18, 0.26) });

    // --- Floor deck ---
    const floor = MeshBuilder.CreateBox('stFloor', { width: HALF_W * 2, height: 0.5, depth: HALF_D * 2 }, this.scene);
    floor.position.set(SX, FLOOR_Y - 0.25, SZ);
    floor.parent = root;
    floor.material = deckMat;
    floor.isPickable = false;
    floor.receiveShadows = true;

    // --- Ceiling ---
    const ceil = MeshBuilder.CreateBox('stCeil', { width: HALF_W * 2, height: 0.5, depth: HALF_D * 2 }, this.scene);
    ceil.position.set(SX, FLOOR_Y + WALL_H, SZ);
    ceil.parent = root;
    ceil.material = wallMat;
    ceil.isPickable = false;

    // --- Walls (4 perimeter slabs) ---
    const mkWall = (n, w, d, x, z) => {
      const wall = MeshBuilder.CreateBox(n, { width: w, height: WALL_H, depth: d }, this.scene);
      wall.position.set(SX + x, FLOOR_Y + WALL_H / 2, SZ + z);
      wall.parent = root;
      wall.material = wallMat;
      wall.isPickable = false;
      return wall;
    };
    mkWall('stWallN', HALF_W * 2, 0.6, 0, -HALF_D);   // far wall (terminal side)
    mkWall('stWallS', HALF_W * 2, 0.6, 0, HALF_D);    // near wall (airlock side)
    mkWall('stWallE', 0.6, HALF_D * 2, HALF_W, 0);
    mkWall('stWallW', 0.6, HALF_D * 2, -HALF_W, 0);

    // Floor accent stripe down the centre.
    const stripe = MeshBuilder.CreateBox('stStripe', { width: 2.2, height: 0.06, depth: HALF_D * 2 - 2 }, this.scene);
    stripe.position.set(SX, FLOOR_Y + 0.05, SZ);
    stripe.parent = root;
    stripe.material = trimMat;
    stripe.isPickable = false;

    // --- Starfield viewport on the east wall ---
    const view = MeshBuilder.CreatePlane('stViewport', { width: 14, height: 4 }, this.scene);
    view.rotation.y = -Math.PI / 2;
    view.position.set(SX + HALF_W - 0.35, FLOOR_Y + 3.6, SZ);
    view.parent = root;
    const viewMat = new StandardMaterial('stViewportMat', this.scene);
    viewMat.diffuseColor = new Color3(0, 0, 0);
    viewMat.emissiveColor = new Color3(0.02, 0.03, 0.08);
    viewMat.disableLighting = true;
    view.material = viewMat;
    view.isPickable = false;
    // Scattered "stars" on the viewport.
    for (let i = 0; i < 26; i++) {
      const star = MeshBuilder.CreatePlane(`stStar_${i}`, { size: 0.08 + rng() * 0.12 }, this.scene);
      star.rotation.y = -Math.PI / 2;
      star.position.set(SX + HALF_W - 0.32, FLOOR_Y + 2.0 + rng() * 3.0, SZ - 6.5 + rng() * 13);
      star.parent = root;
      const sm = new StandardMaterial(`stStarMat_${i}`, this.scene);
      sm.emissiveColor = new Color3(0.8, 0.85, 1.0);
      sm.disableLighting = true;
      star.material = sm;
      star.isPickable = false;
    }

    // --- Trading terminal (far end) ---
    const term = new TransformNode('stTerminal', this.scene);
    term.parent = root;
    term.position.set(this._terminalPos.x, FLOOR_Y, this._terminalPos.z);
    const base = MeshBuilder.CreateBox('stTermBase', { width: 3.0, height: 1.1, depth: 1.2 }, this.scene);
    base.position.set(0, 0.55, 0);
    base.parent = term;
    base.material = this._mat('stTermBaseMat', new Color3(0.2, 0.22, 0.28));
    base.isPickable = false;
    const screen = MeshBuilder.CreateBox('stTermScreen', { width: 2.6, height: 1.6, depth: 0.12 }, this.scene);
    screen.position.set(0, 2.1, -0.4);
    screen.rotation.x = -0.22;
    screen.parent = term;
    const screenMat = new StandardMaterial('stTermScreenMat', this.scene);
    screenMat.diffuseColor = new Color3(0.02, 0.1, 0.14);
    screenMat.emissiveColor = new Color3(0.15, 0.7, 0.85);
    screenMat.disableLighting = true;
    screen.material = screenMat;
    screen.isPickable = false;
    if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(screen); } catch (_e) { /* noop */ } }
    // Floating hologram above the terminal.
    const holo = MeshBuilder.CreateCylinder('stTermHolo', { diameterTop: 0, diameterBottom: 1.0, height: 1.2, tessellation: 5 }, this.scene);
    holo.position.set(0, 3.4, 0);
    holo.parent = term;
    const holoMat = new StandardMaterial('stTermHoloMat', this.scene);
    holoMat.diffuseColor = new Color3(0, 0, 0);
    holoMat.emissiveColor = new Color3(0.2, 0.85, 1.0);
    holoMat.disableLighting = true;
    holoMat.alpha = 0.5;
    holo.material = holoMat;
    holo.isPickable = false;
    if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(holo); } catch (_e) { /* noop */ } }
    this._holo = holo;
    // Terminal accent light.
    const tl = new PointLight('stTermLight', new Vector3(this._terminalPos.x, FLOOR_Y + 2.6, this._terminalPos.z + 1.0), this.scene);
    tl.diffuse = new Color3(0.4, 0.8, 1.0);
    tl.intensity = 0.5;
    tl.range = 14;
    tl.parent = root;

    // --- Airlock door + exit pad (near end) ---
    const frame = MeshBuilder.CreateTorus('stAirlock', { diameter: 4.2, thickness: 0.5, tessellation: 24 }, this.scene);
    frame.rotation.x = Math.PI / 2;
    frame.position.set(this._exitPos.x, FLOOR_Y + 2.0, SZ + HALF_D - 0.4);
    frame.parent = root;
    const frameMat = new StandardMaterial('stAirlockMat', this.scene);
    frameMat.diffuseColor = new Color3(0.1, 0.18, 0.14);
    frameMat.emissiveColor = new Color3(0.2, 1.0, 0.5);
    frameMat.specularColor = new Color3(0, 0, 0);
    frame.material = frameMat;
    frame.isPickable = false;
    if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(frame); } catch (_e) { /* noop */ } }
    const pad = MeshBuilder.CreateDisc('stExitPad', { radius: 2.0, tessellation: 28 }, this.scene);
    pad.rotation.x = Math.PI / 2;
    pad.position.set(this._exitPos.x, FLOOR_Y + 0.08, this._exitPos.z);
    pad.parent = root;
    const padMat = new StandardMaterial('stExitPadMat', this.scene);
    padMat.diffuseColor = new Color3(0, 0, 0);
    padMat.emissiveColor = new Color3(0.2, 0.9, 0.45);
    padMat.disableLighting = true;
    padMat.alpha = 0.55;
    pad.material = padMat;
    pad.isPickable = false;

    // --- Shipyard terminal (west side) — opens the Hangar ---
    const yard = new TransformNode('stShipyard', this.scene);
    yard.parent = root;
    yard.position.set(this._shipyardPos.x, FLOOR_Y, this._shipyardPos.z);
    const yBase = MeshBuilder.CreateBox('stYardBase', { width: 1.2, height: 1.1, depth: 3.0 }, this.scene);
    yBase.position.set(0, 0.55, 0);
    yBase.parent = yard;
    yBase.material = this._mat('stYardBaseMat', new Color3(0.22, 0.2, 0.26));
    yBase.isPickable = false;
    const yScreen = MeshBuilder.CreateBox('stYardScreen', { width: 0.14, height: 1.6, depth: 2.6 }, this.scene);
    yScreen.position.set(0.4, 2.0, 0);
    yScreen.rotation.z = 0.22;
    yScreen.parent = yard;
    const yScreenMat = new StandardMaterial('stYardScreenMat', this.scene);
    yScreenMat.diffuseColor = new Color3(0.12, 0.04, 0.14);
    yScreenMat.emissiveColor = new Color3(0.7, 0.3, 0.9);
    yScreenMat.disableLighting = true;
    yScreen.material = yScreenMat;
    yScreen.isPickable = false;
    if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(yScreen); } catch (_e) { /* noop */ } }
    // Floating ship hologram above the shipyard terminal.
    const yHolo = MeshBuilder.CreateBox('stYardHolo', { width: 0.4, height: 0.3, depth: 1.3 }, this.scene);
    yHolo.position.set(0, 3.2, 0);
    yHolo.parent = yard;
    const yHoloMat = new StandardMaterial('stYardHoloMat', this.scene);
    yHoloMat.diffuseColor = new Color3(0, 0, 0);
    yHoloMat.emissiveColor = new Color3(0.7, 0.4, 1.0);
    yHoloMat.disableLighting = true;
    yHoloMat.alpha = 0.55;
    yHolo.material = yHoloMat;
    yHolo.isPickable = false;
    if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(yHolo); } catch (_e) { /* noop */ } }
    this._yardHolo = yHolo;
    const yl = new PointLight('stYardLight', new Vector3(this._shipyardPos.x + 1.0, FLOOR_Y + 2.6, this._shipyardPos.z), this.scene);
    yl.diffuse = new Color3(0.7, 0.4, 1.0);
    yl.intensity = 0.5;
    yl.range = 14;
    yl.parent = root;

    // --- Neural Console (east side) — opens the Skill Tree ---
    const neural = new TransformNode('stNeural', this.scene);
    neural.parent = root;
    neural.position.set(this._skillsPos.x, FLOOR_Y, this._skillsPos.z);
    const nBase = MeshBuilder.CreateBox('stNeuralBase', { width: 1.2, height: 1.1, depth: 3.0 }, this.scene);
    nBase.position.set(0, 0.55, 0);
    nBase.parent = neural;
    nBase.material = this._mat('stNeuralBaseMat', new Color3(0.16, 0.24, 0.26));
    nBase.isPickable = false;
    const nScreen = MeshBuilder.CreateBox('stNeuralScreen', { width: 0.14, height: 1.6, depth: 2.6 }, this.scene);
    nScreen.position.set(-0.4, 2.0, 0);
    nScreen.rotation.z = -0.22;
    nScreen.parent = neural;
    const nScreenMat = new StandardMaterial('stNeuralScreenMat', this.scene);
    nScreenMat.diffuseColor = new Color3(0.02, 0.14, 0.12);
    nScreenMat.emissiveColor = new Color3(0.2, 0.9, 0.7);
    nScreenMat.disableLighting = true;
    nScreen.material = nScreenMat;
    nScreen.isPickable = false;
    if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(nScreen); } catch (_e) { /* noop */ } }
    // Floating "neural lattice" hologram (a small icosphere) above the console.
    const nHolo = MeshBuilder.CreatePolyhedron('stNeuralHolo', { type: 3, size: 0.6 }, this.scene);
    nHolo.position.set(0, 3.2, 0);
    nHolo.parent = neural;
    const nHoloMat = new StandardMaterial('stNeuralHoloMat', this.scene);
    nHoloMat.diffuseColor = new Color3(0, 0, 0);
    nHoloMat.emissiveColor = new Color3(0.2, 1.0, 0.8);
    nHoloMat.disableLighting = true;
    nHoloMat.alpha = 0.55;
    nHolo.material = nHoloMat;
    nHolo.isPickable = false;
    if (this.glow) { try { this.glow.addIncludedOnlyMesh?.(nHolo); } catch (_e) { /* noop */ } }
    this._neuralHolo = nHolo;
    const nl = new PointLight('stNeuralLight', new Vector3(this._skillsPos.x - 1.0, FLOOR_Y + 2.6, this._skillsPos.z), this.scene);
    nl.diffuse = new Color3(0.2, 1.0, 0.8);
    nl.intensity = 0.5;
    nl.range = 14;
    nl.parent = root;

    // --- Crates / decor ---
    for (let i = 0; i < 5; i++) {
      const cw = 0.9 + rng() * 0.8;
      const crate = MeshBuilder.CreateBox(`stCrate_${i}`, { width: cw, height: cw, depth: cw }, this.scene);
      const side = i % 2 === 0 ? -1 : 1;
      crate.position.set(SX + side * (HALF_W - 2 - rng() * 2), FLOOR_Y + cw / 2, SZ - HALF_D + 4 + rng() * (HALF_D));
      crate.rotation.y = rng() * Math.PI;
      crate.parent = root;
      crate.material = this._mat(`stCrateMat_${i}`, new Color3(0.3, 0.27, 0.2), { emissive: new Color3(0.05, 0.04, 0.02) });
      crate.isPickable = false;
    }

    // --- Station crew (a couple of static figures for life) ---
    for (let i = 0; i < 2; i++) {
      const crew = MeshBuilder.CreateCapsule(`stCrew_${i}`, { radius: 0.32, height: 1.7, tessellation: 10 }, this.scene);
      crew.position.set(SX + (i === 0 ? -6 : 7), FLOOR_Y + 0.85, SZ + (i === 0 ? -2 : 4));
      crew.rotation.y = rng() * Math.PI * 2;
      crew.parent = root;
      const cm = this._mat(`stCrewMat_${i}`, i === 0 ? new Color3(0.7, 0.4, 0.35) : new Color3(0.35, 0.5, 0.65));
      crew.material = cm;
      crew.isPickable = false;
      const head = MeshBuilder.CreateSphere(`stCrewHead_${i}`, { diameter: 0.42, segments: 10 }, this.scene);
      head.position.set(crew.position.x, FLOOR_Y + 1.75, crew.position.z);
      head.parent = root;
      head.material = this._mat(`stCrewHeadMat_${i}`, new Color3(0.85, 0.72, 0.6));
      head.isPickable = false;
    }

    // --- Ceiling lighting (bright, even interior) ---
    const lampZs = [-HALF_D * 0.5, HALF_D * 0.5];
    for (let i = 0; i < lampZs.length; i++) {
      const panel = MeshBuilder.CreateBox(`stLampPanel_${i}`, { width: HALF_W * 1.3, height: 0.12, depth: 1.4 }, this.scene);
      panel.position.set(SX, FLOOR_Y + WALL_H - 0.3, SZ + lampZs[i]);
      panel.parent = root;
      const pm = new StandardMaterial(`stLampPanelMat_${i}`, this.scene);
      pm.diffuseColor = new Color3(0, 0, 0);
      pm.emissiveColor = new Color3(0.9, 0.95, 1.0);
      pm.disableLighting = true;
      panel.material = pm;
      panel.isPickable = false;
      const lamp = new PointLight(`stLamp_${i}`, new Vector3(SX, FLOOR_Y + WALL_H - 0.6, SZ + lampZs[i]), this.scene);
      lamp.diffuse = new Color3(0.95, 0.97, 1.0);
      lamp.intensity = 0.85;
      lamp.range = 48;
      lamp.parent = root;
    }

    this._name = name || 'Station';
    return root;
  }

  showInterior(visible) {
    if (this._root) this._root.setEnabled(!!visible);
  }

  clearInterior() {
    if (this._root) {
      try { this._root.dispose(false, true); } catch (_e) { /* noop */ }
      this._root = null;
    }
    this._holo = null;
    this._yardHolo = null;
    this._neuralHolo = null;
  }

  tick(dt) {
    this._t += dt;
    if (this._holo) {
      this._holo.rotation.y += dt * 1.2;
      this._holo.position.y = 3.4 + Math.sin(this._t * 1.6) * 0.12;
    }
    if (this._yardHolo) {
      this._yardHolo.rotation.y += dt * 0.9;
      this._yardHolo.position.y = 3.2 + Math.sin(this._t * 1.3 + 1) * 0.12;
    }
    if (this._neuralHolo) {
      this._neuralHolo.rotation.y += dt * 1.1;
      this._neuralHolo.rotation.x += dt * 0.5;
      this._neuralHolo.position.y = 3.2 + Math.sin(this._t * 1.5 + 2) * 0.12;
    }
  }

  dispose() {
    this.clearInterior();
  }
}
